/**
 * LEGACY / DEPRECATED (Pic2Content Core 1.0, Phase 0.5 — Generation 2:
 * SourceImage). Reachable only by direct URL (/edit/:imageId) — not
 * linked from AppLayout's nav. Do not build new product features on this
 * page; the current product surface is Home.tsx + editor modals
 * (AssetChain). See docs/architecture/PIC2CONTENT_CORE_1_0.md.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Modal, Select, Slider, Space, Tag, Tooltip, message } from 'antd'
import {
  AimOutlined,
  BorderOutlined,
  DragOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
  MinusOutlined,
  PlusOutlined,
  RedoOutlined,
  ReloadOutlined,
  SaveOutlined,
  ScissorOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import {
  cutoutSourceImage,
  getJob,
  getSourceImage,
  listEngines,
  saveManualMask,
  saveTransparent,
  type SourceImageOut,
} from '../api/client'
import DualPaneCutoutEditor, {
  type BgMode,
  type DualPaneEditorHandle,
  type Polarity,
  type ToolShape,
} from '../components/editor/DualPaneCutoutEditor'
import type { EngineInfo } from '../types'
import { sourceStatusColor } from '../utils/status'

type Phase = 'idle' | 'loading-model' | 'generating-mask' | 'optimizing-edge' | 'generating-preview' | 'failed'

export default function SourceEditorPage() {
  const { imageId } = useParams()
  const navigate = useNavigate()
  const editorRef = useRef<DualPaneEditorHandle>(null)

  const [detail, setDetail] = useState<SourceImageOut | null>(null)
  const [engines, setEngines] = useState<EngineInfo[]>([])
  const [engineName, setEngineName] = useState<string | undefined>()
  const [phase, setPhase] = useState<Phase>('idle')
  const [phaseMessage, setPhaseMessage] = useState('')
  const [phaseProgress, setPhaseProgress] = useState(0)
  const [phaseError, setPhaseError] = useState('')

  const [tool, setTool] = useState<ToolShape>('brush')
  const [polarity, setPolarity] = useState<Polarity>('add')
  const [brushSize, setBrushSize] = useState(32)
  const [brushHardness, setBrushHardness] = useState(0.7)
  const [brushOpacity, setBrushOpacity] = useState(1)
  const [overlayOpacity, setOverlayOpacity] = useState(0.4)
  const [showOverlay, setShowOverlay] = useState(true)
  const [bgMode, setBgMode] = useState<BgMode>('checker')
  const [dirty, setDirty] = useState(false)
  const [scale, setScale] = useState(1)
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 })
  const [saving, setSaving] = useState(false)
  const [confirmEngineOpen, setConfirmEngineOpen] = useState(false)
  const [maskPath, setMaskPath] = useState<string | null>(null)
  const [reloadToken, setReloadToken] = useState(0)

  const maskUrl = maskPath ? `${maskPath}${maskPath.includes('?') ? '&' : '?'}v=${reloadToken}` : null

  const pollTimer = useRef<number | null>(null)

  const loadDetail = useCallback(async () => {
    if (!imageId) return null
    const { data } = await getSourceImage(imageId)
    setDetail(data)
    return data
  }, [imageId])

  const runCutout = useCallback(
    async (engine?: string) => {
      if (!imageId) return
      setPhase('loading-model')
      setPhaseMessage('正在加载模型')
      setPhaseProgress(0.05)
      setPhaseError('')
      try {
        const { data } = await cutoutSourceImage(imageId, engine)
        const jobId = data.job.id
        await new Promise<void>((resolve, reject) => {
          if (pollTimer.current) window.clearInterval(pollTimer.current)
          pollTimer.current = window.setInterval(async () => {
            try {
              const { data: job } = await getJob(jobId)
              setPhaseProgress(job.progress || 0)
              const msg = job.message || job.stage || ''
              setPhaseMessage(msg)
              if (msg.includes('蒙版')) setPhase('generating-mask')
              else if (msg.includes('边缘')) setPhase('optimizing-edge')
              else if (msg.includes('预览') || msg.includes('透明')) setPhase('generating-preview')
              if (job.status === 'succeeded') {
                if (pollTimer.current) window.clearInterval(pollTimer.current)
                pollTimer.current = null
                resolve()
              } else if (job.status === 'failed' || job.status === 'cancelled') {
                if (pollTimer.current) window.clearInterval(pollTimer.current)
                pollTimer.current = null
                reject(new Error(job.message || '自动抠图失败'))
              }
            } catch (e) {
              // keep polling on transient errors
            }
          }, 400)
        })
        const updated = await loadDetail()
        setMaskPath(updated?.mask_url || null)
        setReloadToken((t) => t + 1)
        setDirty(false)
        setPhase('idle')
      } catch (e) {
        setPhase('failed')
        setPhaseError((e as Error).message)
      }
    },
    [imageId, loadDetail],
  )

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const [d, engResp] = await Promise.all([loadDetail(), listEngines()])
      if (!mounted) return
      setEngines(engResp.data)
      setEngineName(engResp.data.find((e) => e.is_default)?.name || engResp.data[0]?.name)
      if (!d) return
      if (d.mask_url) {
        setMaskPath(d.mask_url)
        setReloadToken((t) => t + 1)
      } else if (d.status === 'pending') {
        await runCutout()
      }
    })()
    return () => {
      mounted = false
      if (pollTimer.current) window.clearInterval(pollTimer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageId])

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const goBack = () => {
    if (dirty && !window.confirm('存在未保存的修改，确定离开吗？')) return
    navigate('/')
  }

  const onSave = async () => {
    if (!imageId || !editorRef.current) return
    setSaving(true)
    try {
      const blob = await editorRef.current.exportMaskBlob()
      await saveManualMask(imageId, blob)
      const { data } = await saveTransparent(imageId)
      setDetail(data)
      setDirty(false)
      message.success(`透明底图已保存：${data.relative_path.replace(/\.[^.]+$/, '')}_transparent.png`)
    } catch (e) {
      message.error((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const confirmRerun = () => {
    setConfirmEngineOpen(false)
    runCutout(engineName)
  }

  const isBusy = phase !== 'idle' && phase !== 'failed'

  return (
    <div className="editor-shell">
      <div className="editor-topbar">
        <Space>
          <Button size="small" onClick={goBack}>
            ← 返回图片列表
          </Button>
          <strong className="editor-filename">{detail?.filename || '加载中…'}</strong>
          {detail && <Tag color={sourceStatusColor[detail.status]}>{detail.status_label}</Tag>}
          {dirty && <Tag color="gold">未保存修改</Tag>}
        </Space>
        <Space wrap>
          <Select
            size="small"
            style={{ width: 170 }}
            value={engineName}
            onChange={setEngineName}
            options={engines.map((e) => ({
              value: e.name,
              label: `${e.description || e.name}${e.available ? '' : '（未安装）'}`,
              disabled: !e.available,
            }))}
          />
          <Button size="small" icon={<ReloadOutlined />} disabled={isBusy} onClick={() => setConfirmEngineOpen(true)}>
            重新自动抠图
          </Button>
          <Button size="small" icon={<UndoOutlined />} onClick={() => editorRef.current?.undo()}>
            撤销
          </Button>
          <Button size="small" icon={<RedoOutlined />} onClick={() => editorRef.current?.redo()}>
            重做
          </Button>
          <Button size="small" onClick={() => editorRef.current?.clearPendingEdits()}>
            重置
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={isBusy}
            onClick={onSave}
          >
            保存透明底图
          </Button>
        </Space>
      </div>

      {isBusy && (
        <div className="editor-status-banner">
          <LoadingOutlined spin /> {phaseMessage || '处理中…'}
          <span className="editor-status-progress">{Math.round(phaseProgress * 100)}%</span>
        </div>
      )}
      {phase === 'failed' && (
        <div className="editor-status-banner editor-status-error">
          自动抠图失败：{phaseError}
          <Button size="small" style={{ marginLeft: 12 }} onClick={() => runCutout(engineName)}>
            重试
          </Button>
        </div>
      )}

      <div className="editor-toolstrip">
        <Space wrap size={6}>
          <Tooltip title="增加选区画笔">
            <Button
              size="small"
              type={tool === 'brush' && polarity === 'add' ? 'primary' : 'default'}
              icon={<EditOutlined />}
              onClick={() => {
                setTool('brush')
                setPolarity('add')
              }}
            >
              增加
            </Button>
          </Tooltip>
          <Tooltip title="减少选区画笔">
            <Button
              size="small"
              type={tool === 'brush' && polarity === 'subtract' ? 'primary' : 'default'}
              icon={<ScissorOutlined />}
              onClick={() => {
                setTool('brush')
                setPolarity('subtract')
              }}
            >
              减少
            </Button>
          </Tooltip>
          <Tooltip title="正向点击（点一下即保留该处）">
            <Button
              size="small"
              type={tool === 'click' && polarity === 'add' ? 'primary' : 'default'}
              icon={<AimOutlined />}
              onClick={() => {
                setTool('click')
                setPolarity('add')
              }}
            >
              正向点击
            </Button>
          </Tooltip>
          <Tooltip title="负向点击（点一下即删除该处）">
            <Button
              size="small"
              type={tool === 'click' && polarity === 'subtract' ? 'primary' : 'default'}
              icon={<AimOutlined />}
              onClick={() => {
                setTool('click')
                setPolarity('subtract')
              }}
            >
              负向点击
            </Button>
          </Tooltip>
          <Tooltip title="矩形框选">
            <Button
              size="small"
              type={tool === 'rect' ? 'primary' : 'default'}
              icon={<BorderOutlined />}
              onClick={() => setTool('rect')}
            >
              框选
            </Button>
          </Tooltip>
          <Tooltip title="套索（双击结束）">
            <Button
              size="small"
              type={tool === 'lasso' ? 'primary' : 'default'}
              icon={<EllipsisOutlined />}
              onClick={() => setTool('lasso')}
            >
              套索
            </Button>
          </Tooltip>
          {(tool === 'rect' || tool === 'lasso') && (
            <Select
              size="small"
              value={polarity}
              onChange={setPolarity}
              style={{ width: 90 }}
              options={[
                { value: 'add', label: '增加' },
                { value: 'subtract', label: '减少' },
              ]}
            />
          )}
          <Tooltip title="移动画布（或按住空格键拖动）">
            <Button
              size="small"
              type={tool === 'pan' ? 'primary' : 'default'}
              icon={<DragOutlined />}
              onClick={() => setTool('pan')}
            >
              平移
            </Button>
          </Tooltip>
          <Button size="small" onClick={() => editorRef.current?.invertSelection()}>
            反选
          </Button>
        </Space>

        <Space wrap size={6}>
          <span className="toolstrip-label">画笔大小</span>
          <Slider style={{ width: 90 }} min={2} max={200} value={brushSize} onChange={setBrushSize} />
          <span className="toolstrip-label">硬度</span>
          <Slider style={{ width: 70 }} min={0} max={1} step={0.05} value={brushHardness} onChange={setBrushHardness} />
          <span className="toolstrip-label">不透明度</span>
          <Slider style={{ width: 70 }} min={0.1} max={1} step={0.05} value={brushOpacity} onChange={setBrushOpacity} />
        </Space>

        <Space wrap size={6}>
          <span className="toolstrip-label">选区透明度</span>
          <Slider style={{ width: 80 }} min={0.1} max={0.9} step={0.05} value={overlayOpacity} onChange={setOverlayOpacity} />
          <Button
            size="small"
            icon={showOverlay ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            onClick={() => setShowOverlay((v) => !v)}
          >
            {showOverlay ? '隐藏选区' : '显示选区'}
          </Button>
          <Select
            size="small"
            value={bgMode}
            onChange={setBgMode}
            style={{ width: 100 }}
            options={[
              { value: 'checker', label: '棋盘格' },
              { value: 'white', label: '白底' },
              { value: 'black', label: '黑底' },
              { value: 'gray', label: '灰底' },
            ]}
          />
        </Space>
      </div>

      {detail && (
        <DualPaneCutoutEditor
          ref={editorRef}
          proxyUrl={detail.proxy_url || ''}
          maskUrl={maskUrl}
          reloadToken={reloadToken}
          tool={tool}
          polarity={polarity}
          brushSize={brushSize}
          brushHardness={brushHardness}
          brushOpacity={brushOpacity}
          overlayOpacity={overlayOpacity}
          showOverlay={showOverlay}
          bgMode={bgMode}
          onDirtyChange={setDirty}
          onScaleChange={setScale}
          onImageSize={(w, h) => setImgSize({ w, h })}
        />
      )}

      <div className="editor-bottombar">
        <Space size={10}>
          <span>缩放 {Math.round(scale * 100)}%</span>
          <span>
            {imgSize.w}×{imgSize.h}
          </span>
          <span>引擎：{detail?.engine_name || '-'}</span>
          <span>模型：{detail?.model_name || '-'}</span>
        </Space>
        <Space size={4}>
          <Button size="small" icon={<MinusOutlined />} onClick={() => editorRef.current?.zoomTo(scale / 1.2)} />
          <Button size="small" onClick={() => editorRef.current?.fitToView()}>
            适应窗口
          </Button>
          <Button size="small" onClick={() => editorRef.current?.zoomTo(1)}>
            100%
          </Button>
          <Button size="small" icon={<PlusOutlined />} onClick={() => editorRef.current?.zoomTo(scale * 1.2)} />
        </Space>
      </div>

      <Modal
        title="重新自动抠图"
        open={confirmEngineOpen}
        onOk={confirmRerun}
        onCancel={() => setConfirmEngineOpen(false)}
        okText="继续"
        cancelText="取消"
      >
        重新自动抠图将使用新模型替换当前未保存的蒙版。是否继续？
      </Modal>
    </div>
  )
}
